import { useCallback, useEffect, FocusEvent } from 'react';
import { Field, useField, useFormikContext } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';
import { Alert, Button, Typography } from '@mui/material';

import { FormRowsDivider, InputField, MultiSelect } from '../../../fields';
import { api } from '../../../../api';
import { ApiError } from '../../../errors';
import { SendTestLead } from '../../send-test-lead';

export type GetCourseSettings = {
  apiKey: string;
  apiUrl: string;
  groups?: string[];
};

export const GetCourseIntegration = () => {
  const [{ value: getCourseIntegration }] = useField<GetCourseSettings>('campaign.getCourseIntegration');
  const [{ value: campaignId }] = useField('campaign.id');
  const [getGroups, groupsData] = api.useLazyGetGetCourseGroupsQuery();

  const { setFieldValue, submitForm } = useFormikContext();

  const { apiKey, apiUrl } = getCourseIntegration || {};

  const fetchGroups = useCallback(async () => {
    if (!apiKey || !apiUrl) {
      return;
    }

    await getGroups({ apiKey, apiUrl });
  }, [apiKey, apiUrl, getGroups]);

  const validate = (value: string, schema: Yup.AnySchema) => {
    try {
      schema.validateSync(value);
    } catch (error) {
      return (error as any).message;
    }
  };

  useEffect(() => {
    fetchGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Wrapper>
      <FormRowsDivider />

      <Typography sx={{ fontWeight: 'bold', marginBottom: '12px' }}>GetCourse</Typography>

      <Alert severity="info" variant="filled" sx={{ marginBottom: '24px' }}>
        Чтобы добавлять пользователей в GetCourse, вам нужно сгенерировать ключ API в настройках GetCourse. Ключ нужно
        создать для чтения и записи!
        <br />
        <Button href="https://getcourse.ru/blog/1053682" target="_blank" sx={{ marginLeft: '-8px' }}>
          Перейти в настройки
        </Button>
      </Alert>

      <Field
        name="campaign.getCourseIntegration.apiUrl"
        component={InputField}
        label="Адрес вашего сайта GetCourse"
        className="form-field"
        onBlur={(event: FocusEvent<HTMLInputElement>) => {
          const value = event.target.value;

          if (!value) {
            return;
          }

          try {
            const url = new URL(value);

            setFieldValue('campaign.getCourseIntegration.apiUrl', url.origin).then(() => {
              submitForm();
              fetchGroups();
            });
          } catch (error) {
            console.log(error)
          }
        }}
        validate={(value: string) =>
          validate(value, Yup.string().url('Введите полный адрес сайта'))
        }
        helperText={<>Например, https://dmitrsavk.getcourse.ru</>}
        submitOnBlur={false}
      />

      <Field
        name="campaign.getCourseIntegration.apiKey"
        component={InputField}
        label="Ключ API"
        className="form-field"
        type="password"
        onBlur={fetchGroups}
      />

      {groupsData.data?.groups.length ? (
        <Field
          name="campaign.getCourseIntegration.groups"
          component={MultiSelect}
          label="Добавить пользователя в группы"
          className="form-field"
          disabled={groupsData.isLoading}
          options={groupsData.data.groups.map((group) => ({
            id: group.name,
            label: group.name,
          }))}
        />
      ) : null}

      {groupsData.isError && <ApiError error={groupsData.error} withMargin />}
      <SendTestLead integrationId="get-course" campaignId={campaignId} />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  .form-field {
    margin-bottom: 24px;
    width: 335px;
  }
`;
